<template>
  <div class="min-h-screen xl:flex">
    <!-- Sidebar -->
    <OrganismsDashboardSidebar />
    <!-- Main Content Area -->
    <div class="flex-1">
      <!-- Header -->
      <OrganismsDashboardHeader  v-if="!isPWAInstalled()" />
      <OrganismsNavPwaMobileNav v-if="isPWAInstalled()" class="block md:hidden"/>
      <!-- Page Content -->
      <div 
        class="mx-auto max-w-(--breakpoint-2xl)  transition-all duration-300 ease-in-out"
        :class="[isExpanded || isHovered ? 'lg:ml-[290px]' : 'lg:ml-[88px]']"
      >
        <client-only>
          <MoleculesDashboardVerificationBar
            v-if="(!emailVerified || isGuestUser) && !$route?.path?.includes('/vault/')"
            :is-guest-user="isGuestUser"
            :enable-dashboard-nav="!$route?.path?.includes('account-settings')"
            :is-pwa-app="isPWAInstalled()"
          />
        </client-only>
        <slot />
      </div>

      <div
        v-if="isPWAInstalled() && !hideDashboardMenu"
        id="pwa-nav-bar"
        class="sticky bottom-0 block md:hidden z-10">
        <div
          class="text-2xs content-center text-center text-white transition-opacity duration-1000 ease-in-out font-bold"
          :class="{
            'bg-blue-900 opacity-100 h-[1.25rem]': !isOnline,
            'bg-blue-500 opacity-100 h-[1.25rem]': isOnline && showOnlineMessage,
            'opacity-0 h-0': isOnline && !showOnlineMessage
          }"
        >
          {{ isOnline ? 'Back Online' : 'No Connection' }}
        </div>
        <ul class="flex flex-wrap text-sm font-medium text-center text-gray-500 dark:text-gray-400 justify-between h-[5rem] border-gray-700 bg-white" :class="{'border-t':isOnline && !showOnlineMessage}">
          <li
            v-for="tab in tabs"
            :key="tab.title"
            class="w-1/4">
            <nuxt-link
              exact-active-class="border-t-2 border-t-semantic-secondary-default border-solid font-bold bg-semantic-surface-container-lowest"
              :to="tab.route"
              class="inline-flex flex-col items-center justify-start pt-2 px-4 border-t-2 border-transparent text-dark group w-full h-full">
              <component
                :is="tab.icon"
                fill="currentColor"
                class="w-6 h-6"/>
              <span class="text-xs mt-1">{{ tab.title }}</span>
            </nuxt-link>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
const { isExpanded, isHovered } = useSidebar()
const userStore = useUserStore()
const vaultStore = useVaultStore()

defineOptions({
  name: 'MemberHubLayout'
})

useOneTap()
if (import.meta.client) {
  try {
    // Initialize appcues
    const { $appcuesClient } = useNuxtApp()
    $appcuesClient.init()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Appcues Init:', e)
  }
}

const route = useRoute()

const hideDashboardMenu = computed(() => {
  return !isDashboardPage.value
})
const isDashboardPage = computed(() => {
  return ['dashboard','dashboard-quotes','dashboard-rewards','dashboard-perks'].includes(route.name)
})
const isPageRendered = ref(false)

const waitForIntercom = () => {
  return new Promise(resolve => {
    const interval = setInterval(() => {
      if (typeof window !== 'undefined' && window.Intercom) {
        clearInterval(interval)
        resolve()
      }
    }, 500)
  })
}

onMounted(async () => {
  isPageRendered.value = true
  nextTick().then(() => {
    if(!vaultStore?.verticals?.length) {
      vaultStore.fetchVerticals()
    }
  })
  // Hide intercom launcher
  if (isPWAInstalled()) {
    await waitForIntercom()
    window.Intercom('update', { hide_default_launcher: true })
  }
})

const emailVerified = computed(() => {
  return userStore.isEmailVerified
})
const isGuestUser = computed(() => {
  return userStore.isGuestUser
})

const { isOnline,showOnlineMessage } = useOnlineStatus()

const tabs = [
  {
    title: 'Dashboard',
    route: '/dashboard/',
    icon: resolveComponent('V2IconsHome')
  },
  {
    title: 'Quotes',
    route: '/dashboard/quotes/',
    icon: resolveComponent('V2IconsClipboard')
  },
  {
    title: 'Gift Cards',
    route: '/dashboard/rewards/',
    icon: resolveComponent('V2IconsPartyPopper')
  },
  {
    title: 'Perks',
    route: '/dashboard/perks/',
    icon: resolveComponent('V2IconsGift')
  }
]

usePwaUpdate()
</script>